import Checkbox from '@atlaskit/checkbox';
import { key, subscribeTo } from '@databases/ping-registry';
import { getService } from '@databases/service-container';
import React, { useEffect, useState } from 'react';

interface SelectionsEntryCheckboxProps {
  entryId: string;
  index: number;
}

/**
 * This component is rendered when the databasesSelectionsPerformance experiment flag is enabled.
 * Input events are captured by the selections package for optimized performance.
 *
 * Uses Atlaskit Checkbox in controlled mode (`isChecked`) — state is synchronized
 * through the ping registry, which bridges the selections effect and React cleanly.
 *
 * Checkbox state is synchronized through two mechanisms:
 * 1. **Render-time read**: On mount (including virtualization scroll-in), reads
 *    from `SelectionsApi` to set the correct initial state.
 * 2. **Ping subscription**: After mount, subscribes to the ping registry for
 *    selection changes. The checkbox effect notifies the registry on each delta,
 *    and this component re-renders with the updated `isChecked` value.
 */
const SelectionsEntryCheckbox = ({ entryId, index }: SelectionsEntryCheckboxProps) => {
  const selectionsApi = getService('SelectionsApi');
  const [isChecked, setIsChecked] = useState(() => selectionsApi.isRowSelected(index));

  useEffect(() => {
    const rowKey = key('row', { entryId });

    return subscribeTo(rowKey, 'selected', (selected: boolean) => {
      setIsChecked(selected);
    });
  }, [entryId]);

  return (
    <Checkbox
      className="selections-entry-checkbox"
      isChecked={isChecked}
      data-entry-id={entryId}
      data-row-index={index}
    />
  );
};

export default SelectionsEntryCheckbox;
